import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AppIcon } from "@/components/AppIcon";
import { useNativeApp } from "@/hooks/useNativeApp";
import { Link, ShoppingCart, Store, Wifi } from "lucide-react";

const slides = [
  {
    type: "welcome" as const,
    title: "Your AI Kitchen Assistant",
    description: "Smart recipes, meal planning, and pantry management — all in one app.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/ai-recipe-generation.webp",
    title: "AI Recipe Generation",
    description: "Generate personalized recipes with AI based on your ingredients and preferences.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/meal-planning-new.png",
    title: "Smart Meal Planning",
    description: "Plan your meals for days or weeks ahead with drag-and-drop simplicity.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/pantry-management.webp",
    title: "Pantry Management",
    description: "Track your ingredients, get expiration alerts, and reduce food waste effortlessly.",
  },
  {
    type: "feature" as const,
    image: "/images/landing/recipe-management.webp",
    title: "Recipe Management",
    description: "Create, edit, and organize your recipes. Import from URLs or build from scratch.",
  },
  {
    type: "extras" as const,
    title: "Plus Many More Features",
    description: "Everything you need for a complete cooking experience.",
    items: [
      { icon: "link", label: "URL Import", desc: "Import from any cooking website" },
      { icon: "cart", label: "Smart Grocery Lists", desc: "Pantry-aware shopping lists" },
      { icon: "store", label: "Retailer Links", desc: "Buy from your favorite stores" },
      { icon: "wifi", label: "Offline Access", desc: "Cook without internet" },
    ],
  },
  {
    type: "cta" as const,
    title: "Ready to Cook Smarter?",
    description: "Create your free account and start your culinary journey.",
  },
];

const ExtraIcon = ({ icon }: { icon: string }) => {
  const cls = "w-5 h-5";
  switch (icon) {
    case "link": return <Link className={`${cls} text-purple-600`} />;
    case "cart": return <ShoppingCart className={`${cls} text-green-600`} />;
    case "store": return <Store className={`${cls} text-red-600`} />;
    case "wifi": return <Wifi className={`${cls} text-indigo-600`} />;
    default: return null;
  }
};

export const MobileOnboarding = () => {
  const navigate = useNavigate();
  const { isNativeApp } = useNativeApp();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  const isLast = selectedIndex === slides.length - 1;

  return (
    <main className="fixed inset-0 bg-background flex flex-col">
      {/* Skip */}
      {!isLast && (
        <div className="absolute top-4 right-4 z-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/auth")}
            className="text-muted-foreground"
          >
            Skip
          </Button>
        </div>
      )}

      {/* Carousel */}
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div
              key={i}
              className="flex-[0_0_100%] min-w-0 flex flex-col items-center justify-center px-6 text-center"
            >
              {slide.type === "welcome" && (
                <>
                  <AppIcon size={200} className="mb-8" />
                  <h1 className="text-3xl font-bold text-foreground mb-4">{slide.title}</h1>
                  <p className="text-lg text-muted-foreground max-w-sm">{slide.description}</p>
                </>
              )}

              {slide.type === "feature" && (
                <>
                  <div className="w-full max-w-md h-56 sm:h-64 rounded-2xl overflow-hidden shadow-lg mb-6">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">{slide.title}</h2>
                  <p className="text-base text-muted-foreground max-w-sm">{slide.description}</p>
                </>
              )}

              {slide.type === "extras" && (
                <>
                  <h2 className="text-2xl font-bold text-foreground mb-2">{slide.title}</h2>
                  <p className="text-sm text-muted-foreground mb-6">{slide.description}</p>
                  <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                    {(slide as typeof slides[5]).items.map((item, j) => (
                      <div key={j} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-muted/40">
                        <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shadow-sm">
                          <ExtraIcon icon={item.icon} />
                        </div>
                        <span className="text-sm font-semibold text-foreground">{item.label}</span>
                        <span className="text-xs text-muted-foreground leading-tight">{item.desc}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {slide.type === "cta" && (
                <>
                  <AppIcon size={160} className="mb-6" />
                  <h2 className="text-2xl font-bold text-foreground mb-3">{slide.title}</h2>
                  <p className="text-base text-muted-foreground max-w-sm mb-8">{slide.description}</p>
                  <Button
                    size="lg"
                    onClick={() => navigate("/auth")}
                    className="rounded-full px-10 py-4 text-lg"
                  >
                    Get Started
                  </Button>
                  {!isNativeApp && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/pricing")}
                      className="mt-3 text-muted-foreground"
                    >
                      See Plans
                    </Button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 pb-12">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-all ${
              i === selectedIndex
                ? "bg-primary w-5"
                : "bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    </main>
  );
};
